/* eslint-disable @typescript-eslint/naming-convention */
import React, { useRef, useState } from 'react';

import { getPlanStatus } from '../EmiPlans/helper';
import EsignSuccess from './EsignSuccess';
import Modal from '@/lib/Modal';
import Input from '@/lib/Input';
import Button from '@/lib/Button';
import Icon from '@/lib/Icon/Icon';
import { getESignUrl } from '@/api/kyc';
import { useGlobalStore } from '@/store';
import { onEnterKey } from '@/utils/helpers';
import SecuredByRazorpay from '@/lib/SecuredByRazorpay';
import { sendEmailOtp, verifyEmailOtp } from '@/api/login';
import OpenPopupLoader from '@/shared/Components/OpenPopupLoader';
import {
  CUSTOMER_ACCESS_TOKEN_KEY,
  EMAIL_REGEX,
  ESIGN_SUCCESS,
  NUMBER_REGEX,
  OTP_REGEX,
} from '@/constants';
import { PaymentCancelWrapper } from '@/shared/Components/PaymentCancelWrapper';
import { PopupFailureProps } from '@/shared/types';
import { getUserLoggedInStatus, trackHelper } from '@/analytics/utils';
import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';
import { EmiPlans } from '@/api/types';

const EmailVerificationPopUp: React.FC = () => {
  const [showModal, setShowModal] = useState(true);
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState('');
  const [otpError, setOtpError] = useState('');
  const eSignUrlRef = useRef<string | null>(null);
  const [emailError, setEmailError] = useState('');
  const [isOtpSend, setIsOtpSend] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [didESignFail, setDidESignFail] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [openESignPopup, setOpenESignPopup] = useState(false);
  const setActiveView = useGlobalStore((store) => store.setActiveView);
  const { userAccessToken, user, activeView, lenderDetails, selectedTenure } = useGlobalStore();
  const [secondsToResendOtp, setSecondsToResendOtp] = useState(15);
  const reduceResendInterval = useRef<NodeJS.Timer | null>(null);
  const [eSignError, setEsignError] = useState('');
  const [state, setState] = useState({
    showEsignSuccess: false,
  });

  const resetResendOTPTimer = (): void => {
    setSecondsToResendOtp(15);

    reduceResendInterval.current = setInterval(() => {
      setSecondsToResendOtp((current) => {
        if (current > 0) {
          return current - 1;
        } else {
          clearTimeout(reduceResendInterval.current as NodeJS.Timer);
          return current;
        }
      });
    }, 1000);
  };

  const handleSendOTP = async (): Promise<void> => {
    if (!EMAIL_REGEX.test(email)) {
      return setEmailError('Invalid Email');
    }

    setIsSendingOtp(true);

    const response = await sendEmailOtp({
      body: {
        email,
      },
      headers: {
        [CUSTOMER_ACCESS_TOKEN_KEY]: userAccessToken ?? '',
      },
    });

    // Track send otp click
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      objectName: EVENT_NAMES.SEND_EMAIL_OTP,
      screen: activeView,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        fc_customer_id: user.id,
        selected_plan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
      },
    });

    setIsSendingOtp(false);

    if (response.success) {
      setIsOtpSend(true);
      resetResendOTPTimer();
    } else {
      setEmailError('Unable to Send OTP');
    }
  };

  const handleVerifyOtp = async (): Promise<void> => {
    if (!OTP_REGEX.test(otp)) {
      return setOtpError('Invalid OTP');
    }

    setIsVerifyingOTP(true);

    const response = await verifyEmailOtp({
      body: {
        email,
        otp,
      },
      headers: {
        [CUSTOMER_ACCESS_TOKEN_KEY]: userAccessToken ?? '',
      },
    });

    // Track Submit OTP click
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      objectName: EVENT_NAMES.VERIFY_EMAIL_OTP,
      screen: activeView,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        fc_customer_id: user.id,
        selected_plan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
      },
    });

    if (response.success) {
      const eSignResponse = await getESignUrl();

      if (eSignResponse.success && eSignResponse.url) {
        eSignUrlRef.current = eSignResponse.url;
        // Track Esign loader render
        trackHelper({
          actionName: ANALYTICS_ACTIONS.RENDER,
          objectName: EVENT_NAMES.ESIGN_POPUP_RENDER,
          screen: activeView,
          properties: {
            contact: user.contact,
            user_type: getUserLoggedInStatus(userAccessToken, user.contact),
            fc_customer_id: user.id,
            selected_plan: selectedTenure,
            user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
          },
        });
        setOpenESignPopup(true);
      }
    } else {
      setOtpError('Invalid OTP');
    }
    setIsVerifyingOTP(false);
  };

  const resendOtp = async (): Promise<void> => {
    if (secondsToResendOtp === 0) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      handleSendOTP();
    }
  };

  const handleEsignFailure = (e: PopupFailureProps): void => {
    setDidESignFail(true);
    setOpenESignPopup(false);
    if (e.message) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setEsignError((e.message as unknown as any)?.description || '');
    }

    // Track esign failure
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.ESIGN_POPUP_FAILURE,
      screen: activeView,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        fc_customer_id: user.id,
        selected_plan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
        error_message: e.message ?? '',
      },
    });
  };

  const handleEditEmail = (): void => {
    setIsSendingOtp(false);
    setIsOtpSend(false);
    setEmail('');
    setOtp('');
    clearTimeout(reduceResendInterval.current as NodeJS.Timer);
  };

  const handleEsignSuccess = (data: Record<string, any>): void => {
    if (data.razorpay_payment_id) {
      setOpenESignPopup(false);
      setShowModal(false);
      setState({
        ...state,
        showEsignSuccess: true,
      });
      // Redirect to payments screen after timeout
      setTimeout(() => {
        setActiveView('PAYMENTS');
      }, ESIGN_SUCCESS);
    } else {
      setDidESignFail(true);
      setOpenESignPopup(false);
    }
  };

  const { showEsignSuccess } = state;

  return (
    <>
      <Modal isOpen={showModal} onDismiss={(): void => setShowModal(false)}>
        <Modal.Body>
          <div className="d:w1/2">
            <div>
              <h2 className="font-tasa text-2xl text-on-grey-normal font-semibold mb-6 mr-4">
                {isOtpSend ? (
                  <>
                    Enter the OTP sent to {email}
                    <button
                      className="relative top-0.5 ml-1"
                      onClick={(): void => handleEditEmail()}
                    >
                      <Icon name="edit" className="mx-auto" />
                    </button>
                  </>
                ) : (
                  'Enter your email ID to receive EMI related documents'
                )}
              </h2>
              {isOtpSend ? (
                <>
                  <Input
                    value={otp}
                    error={otpError}
                    onChange={(e): void => {
                      setOtpError('');
                      const value = e.target.value;
                      if (value === '' || NUMBER_REGEX.test(e.target.value)) setOtp(e.target.value);
                    }}
                    onKeyDown={onEnterKey(handleSendOTP)}
                    placeholder="Enter OTP"
                  />
                  {secondsToResendOtp > 0 ? (
                    <div className="w-full flex justify-center">
                      <p className="text-base text-on-grey-muted">
                        Resend OTP in : 00:{secondsToResendOtp}s
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={resendOtp}
                      className="text-base text-on-grey-muted cursor-pointer"
                    >
                      Resend OTP
                    </button>
                  )}
                </>
              ) : (
                <Input
                  value={email}
                  error={emailError}
                  onChange={(e): void => {
                    setEmailError('');
                    setEmail(e.target.value);
                  }}
                  placeholder="Enter Email"
                  onKeyDown={onEnterKey(handleSendOTP)}
                />
              )}
            </div>
            <div className="mt-8">
              {isOtpSend ? (
                <Button onClick={handleVerifyOtp} isLoading={isVerifyingOTP}>
                  Submit OTP and continue to e-Sign
                </Button>
              ) : (
                <Button onClick={handleSendOTP} isLoading={isSendingOtp}>
                  Send OTP
                </Button>
              )}

              <SecuredByRazorpay />
            </div>
          </div>
        </Modal.Body>
      </Modal>
      {openESignPopup ? (
        <OpenPopupLoader
          serviceType="esign"
          onSuccess={handleEsignSuccess}
          onFail={handleEsignFailure}
          url={eSignUrlRef.current as string}
          serviceName="CashE"
          progressText="e-Sign is in progress on"
        />
      ) : null}
      {didESignFail ? (
        <PaymentCancelWrapper
          isOpen={didESignFail}
          onDismiss={(): null => null}
          ctaText="Change payment method"
          title="E-Sign Failed"
          isCloseable={false}
          description={eSignError || 'E-Sign failed. Try with a different payment method'}
        />
      ) : null}
      {showEsignSuccess ? (
        <EsignSuccess
          isOpen={showEsignSuccess}
          onDismiss={(): void => {
            setState({
              ...state,
              showEsignSuccess: false,
            });
          }}
        />
      ) : null}
    </>
  );
};

export default EmailVerificationPopUp;
